import { useMutation } from "@vue/apollo-composable";
import { defineStore } from "pinia";
import { ref } from "vue";
import { useRouter } from "vue-router";

import {
  CONFIRM_PASSWORD_RESET_MUTATION,
  LOGIN_MUTATION,
  LOGOUT_MUTATION,
  REQUEST_PASSWORD_RESET_MUTATION,
} from "../api/authDocuments";

import { apolloClient } from "@/shared/api/apolloClient";
import { tokenManager } from "@/shared/api/tokenManager";
import { useUserStore } from "@/shared/stores/useUserStore";

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();
  const userStore = useUserStore();

  // ── Login ────────────────────────────────────────────────────────────────────

  const loginError = ref<string | null>(null);

  const {
    mutate: loginMutate,
    loading: loginLoading, onError: onLoginError,
  } = useMutation(LOGIN_MUTATION);
  onLoginError((err) => {
    loginError.value = err.message;
  });

  const login = async (email: string, password: string): Promise<void> => {
    loginError.value = null;
    try {
      const result = await loginMutate({ input: { email, password } });
      if (result?.data?.login?.accessToken) {
        tokenManager.setAccessToken(result.data.login.accessToken);
        userStore.clearUser();
        // Always reset state so initialize() re-fetches /me even after a prior failure
        await userStore.initialize();
        if (userStore.isAuthenticated) {
          await router.push("/");
        }
      }
    }
    catch (err) {
      // Catch network-level errors (GraphQL errors are handled by onLoginError)
      if (!loginError.value) {
        loginError.value = err instanceof Error ? err.message : "Login failed";
      }
    }
  };

  // ── Logout ───────────────────────────────────────────────────────────────────

  const { mutate: logoutMutate } = useMutation(LOGOUT_MUTATION);

  const logout = async (): Promise<void> => {
    try {
      await logoutMutate({});
    }
    catch (err) {
      console.error("Logout error:", err);
    }
    finally {
      tokenManager.clearTokens();
      await apolloClient.clearStore();
      userStore.clearUser();
      await router.push("/auth");
    }
  };

  // ── Request password reset ────────────────────────────────────────────────────

  const {
    mutate: requestPasswordResetMutate,
    loading: forgotPasswordLoading,
  } = useMutation(REQUEST_PASSWORD_RESET_MUTATION);

  const getResetPasswordLink = async (email: string, callback?: () => void): Promise<void> => {
    const result = await requestPasswordResetMutate({ input: { email } });
    if (result) callback?.();
  };

  // ── Confirm password reset ────────────────────────────────────────────────────

  const {
    mutate: confirmPasswordResetMutate,
    loading: resetPasswordLoading,
  } = useMutation(CONFIRM_PASSWORD_RESET_MUTATION);

  const resetPassword = async (
    token: string,
    newPassword: string,
    callback?: () => void,
  ): Promise<void> => {
    const result = await confirmPasswordResetMutate({ input: { token, newPassword } });
    if (result) callback?.();
  };

  return {
    loginLoading,
    loginError,
    forgotPasswordLoading,
    resetPasswordLoading,

    login,
    logout,
    getResetPasswordLink,
    resetPassword,
  };
});
